import React, { useState } from "react";
import axios from "axios";
import { Check } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { CITIES_SELECT } from "../mock";
import { trackEvent } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#F1F4F8] border border-[#DEE6EE] rounded-xl px-4 py-3 text-[14px] text-[#1B2A4A] placeholder:text-[#aeb7c2] outline-none focus:border-[#159A90] focus:ring-2 focus:ring-[#159A90]/20 transition";

const EnquiryForm = ({
  category = "Enquiry",
  extraLabel = "Area of interest",
  extraOptions = [],
  submitLabel = "Submit",
  perks = [],
}) => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    extra: "",
    notes: "",
  });

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your name.";
    const digits = form.phone.replace(/\D/g, "").replace(/^(91|0)/, "");
    if (!digits) errs.phone = "Please enter your phone number.";
    else if (!/^[6-9]\d{9}$/.test(digits)) errs.phone = "Enter a valid 10-digit mobile number.";
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim()))
      errs.email = "Please enter a valid email address.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast({ title: "Please check the form", description: "Some details need fixing before we can submit." });
      return;
    }
    setSubmitting(true);
    try {
      const digits = form.phone.replace(/\D/g, "").replace(/^(91|0)/, "");
      await axios.post(`${API}/bookings`, {
        name: form.name,
        phone: digits,
        email: form.email,
        city: form.city,
        service: `${category}: ${form.extra || "General"}`,
        notes: form.notes,
      });
      if (category === "Partnership") trackEvent("partner_form_submit");
      else if (category === "Career") trackEvent("careers_form_submit");
      else trackEvent("contact_form_submit", { form_name: category.toLowerCase() });
      toast({ title: "Thank you ✓", description: `We've received your ${category.toLowerCase()} and will be in touch soon.` });
      setForm({ name: "", phone: "", email: "", city: "", extra: "", notes: "" });
      setErrors({});
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again, or call us at +91 96117 61212." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-[28px] p-7 md:p-9 border border-[#DEE6EE] shadow-[0_24px_60px_rgba(0,0,0,0.1)]">
      {perks.length > 0 && (
        <ul className="space-y-2.5 mb-6">
          {perks.map((p) => (
            <li key={p} className="flex items-center gap-3">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#159A90]/12 shrink-0">
                <Check size={13} className="text-[#159A90]" />
              </span>
              <span className="text-[14px] text-[#3a4757]">{p}</span>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-[13px] font-semibold text-[#3a4757] mb-1.5">Full name</label>
            <input className={`${inputCls} ${errors.name ? "border-red-400" : ""}`} placeholder="e.g. Ananya Sharma" value={form.name} onChange={(e) => update("name", e.target.value)} />
            {errors.name && <p className="text-[12px] text-red-500 mt-1.5">{errors.name}</p>}
          </div>
          <div>
            <label className="block text-[13px] font-semibold text-[#3a4757] mb-1.5">Phone</label>
            <input type="tel" inputMode="numeric" maxLength={10} className={`${inputCls} ${errors.phone ? "border-red-400" : ""}`} placeholder="10-digit mobile number" value={form.phone} onChange={(e) => update("phone", e.target.value.replace(/\D/g, "").slice(0, 10))} />
            {errors.phone && <p className="text-[12px] text-red-500 mt-1.5">{errors.phone}</p>}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-[13px] font-semibold text-[#3a4757] mb-1.5">Email</label>
            <input type="email" className={`${inputCls} ${errors.email ? "border-red-400" : ""}`} placeholder="you@example.com" value={form.email} onChange={(e) => update("email", e.target.value)} />
            {errors.email && <p className="text-[12px] text-red-500 mt-1.5">{errors.email}</p>}
          </div>
          <div>
            <label className="block text-[13px] font-semibold text-[#3a4757] mb-1.5">City</label>
            <Select value={form.city} onValueChange={(v) => update("city", v)}>
              <SelectTrigger className="w-full bg-[#F1F4F8] border-[#DEE6EE] rounded-xl px-4 py-6 text-[14px] focus:ring-2 focus:ring-[#159A90]/20">
                <SelectValue placeholder="Select city" />
              </SelectTrigger>
              <SelectContent>
                {CITIES_SELECT.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="block text-[13px] font-semibold text-[#3a4757] mb-1.5">{extraLabel}</label>
          <Select value={form.extra} onValueChange={(v) => update("extra", v)}>
            <SelectTrigger className="w-full bg-[#F1F4F8] border-[#DEE6EE] rounded-xl px-4 py-6 text-[14px] focus:ring-2 focus:ring-[#159A90]/20">
              <SelectValue placeholder={`Choose ${extraLabel.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {extraOptions.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-[13px] font-semibold text-[#3a4757] mb-1.5">Message (optional)</label>
          <textarea className={`${inputCls} resize-none h-24`} placeholder="Tell us a little more..." value={form.notes} onChange={(e) => update("notes", e.target.value)} />
        </div>
        <button type="submit" disabled={submitting} className="w-full px-6 py-3.5 rounded-full bg-[#159A90] text-white font-semibold hover:bg-[#0E7A72] transition-colors shadow-[0_10px_30px_rgba(21,154,144,0.35)] disabled:opacity-70">
          {submitting ? "Sending…" : submitLabel}
        </button>
      </form>
    </div>
  );
};

export default EnquiryForm;
